import React, { useState, useEffect, useMemo, useContext, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';

import { ErrorBoundary, LogoLoader, TermSheetProvider } from 'components/UI';
import { ProtectedRoute, ProtectedAuthRoute } from 'components/router';
import { LayoutDefault, LayoutFrame } from 'layouts';
import { CertificatesPage } from 'pages/Certificates';
import { PendingSalePage } from 'pages/PendingSalePage';
import { CertificatePage } from 'pages/CertificatePage';
import { SharePage } from 'pages/SharePage';
import { ProvenanceSubView } from 'pages/CertificatePage/subviews/ProvenanceSubView';
import { ScansSubView } from 'pages/CertificatePage/subviews/ScansSubView';
import { ActivitySubView } from 'pages/CertificatePage/subviews/ActivitySubView';
import { ExperienceSubView } from 'pages/CertificatePage/subviews/ExperienceSubView';
import { NotFound } from 'pages/NotFound';
import { ProfilePage } from 'pages/Profile';
import { NotificationsPage } from 'pages/Notifications';
import { HelpPage } from 'pages/Help';
import { HelpArticlePage } from 'pages/Help/ArticlePage';
import { MyCodePage } from 'pages/MyCode';
import { ActivityPage } from 'pages/Activity';
import { SignInBanner } from 'pages/SignIn/SignInBanner';
import { ForgotPassword } from 'pages/ForgotPassword';
import AuthCallback from 'pages/AuthCallback';
import SignupPage from 'pages/Signup';
import SignupSuccessPage from 'pages/SignupSuccess';
import CompanySuspended from 'pages/CompanySuspended';
import ProfileCode from 'pages/ProfileCode';
import CreateAccountEmail from 'pages/onboarding/CreateAccountEmail';
import ClaimOwnershipSignUp from 'pages/onboarding/ClaimOwnershipSignUp';

import { creteMuiTheme } from 'settings/mui/createMuiTheme';
import { checkAuthAction, setShopifyUser, setScanHash, loginShopifyUser } from 'store/auth/actions';
import { storage, extractSubdomain, applyTailwindThemeColors, decodeJwtPayload } from 'utils';
import { dismissScanLoader } from 'utils/scanLoader';

import { ROUTES } from 'routes';
import { IGetShopifySearchParams } from 'types';

import InvalidScans from 'pages/InvalidScans';
import TagReplaced from 'pages/TagReplaced';
import TagNotActive from 'pages/TagNotActive';
import CertificateNotActive from 'pages/CertificateNotActive';
import SomethingWentWrong from 'pages/SomethingWentWrong';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { useSnackbar } from 'components/Snackbar';
import { isEmpty } from 'utils/parsers';
import { useGetCompanyBySubdomain, useGetCompanyByEmbedKey, useGetCompanyStyles } from 'hooks/api';
import { StudioPreviewPage } from 'pages/StudioPreviewPage';
import { getShadowRoot } from 'src';
import { embeddedAppPageCorrections } from './utils/embeddedAppPageCorrection';
import { CompanyContext } from 'contexts/companyContext';
import { ImpersonationBanner } from 'components/ImpersonationBanner';

const App = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isIframe = window.self !== window.top;

  const [isLoading, setIsLoading] = useState(true);
  const { openSnackbar } = useSnackbar();
  const { setCompanyId, companyId } = useContext(CompanyContext);

  const currentUrl = window.location.href;
  const url = new URL(currentUrl);

  const certificate_data: string | null = url.searchParams.get('certificate_data');
  const token: string | null = url.searchParams.get('token');

  // Try to get subdomain from hostname
  const subdomain = useMemo(() => extractSubdomain(), []);

  // Try to get embedd_key from URL params or script data attributes
  const embeddKey = useMemo(() => {
    const urlParam = url.searchParams.get('embedd_key');
    if (urlParam) {
      return urlParam;
    }

    // Check script tag data attributes
    const scripts = document.querySelectorAll('script[data-embedded-key]');

    // Log all scripts for debugging
    const allScripts = document.querySelectorAll('script');
    allScripts.forEach((script, index) => {
      const src = script.getAttribute('src') || 'inline';
      const dataAttrs: string[] = [];
      Array.from(script.attributes).forEach(attr => {
        if (attr.name.startsWith('data-')) {
          dataAttrs.push(`${attr.name}="${attr.value}"`);
        }
      });
    });

    for (const script of Array.from(scripts)) {
      const embeddedKey = script.getAttribute('data-embedded-key');
      if (embeddedKey) {
        return embeddedKey;
      }
    }

    return null;
  }, []);

  // VRG-91 (Go into Space): under impersonation apps/app loads at the bare app URL
  // (no subdomain / embed key), so the normal company resolution can't find the
  // company and company-scoped requests fire with an unresolved id
  // (e.g. /companies/public/undefined/settings/style). The impersonation token
  // carries the real company_id — use it as the source of truth here.
  const impersonationCompanyId = useMemo(() => {
    const tok = url.searchParams.get('impersonation_token') || storage.getItem('accessToken', '');
    if (!tok) return null;
    const payload = decodeJwtPayload(tok);
    return payload?.impersonatingAs?.companyId ?? payload?.company_id ?? null;
  }, []);

  // VRG-809 — the Studio preview's explicit company. The preview iframe loads the BARE app
  // origin (app.vera.inc): no subdomain, no embed key, and no impersonation token, so the
  // chain below used to end at the hardcoded seed id and render the seed brand for EVERY
  // brand's Studio. Read only alongside `isPreview`, so this is not a way to re-point a
  // real tag's page by appending a query param — a public certificate still resolves by
  // subdomain or embed key exactly as before.
  const previewCompanyId = useMemo(() => {
    if (url.searchParams.get('isPreview') !== 'true') return null;
    return url.searchParams.get('company_id');
  }, []);

  const { data: companyTheme, isLoading: isCompanyThemeLoading } = useGetCompanyStyles({
    companyId: companyId || impersonationCompanyId || undefined,
  });

  // Priority 1: Try to get company by subdomain
  const {
    data: companyBySubdomain,
    isLoading: isLoadingSubdomain,
    error: subdomainError,
  } = useGetCompanyBySubdomain(subdomain);

  // Priority 2: Try to get company by embed key (only if no subdomain)
  const shouldFetchByEmbedKey = !subdomain ? embeddKey : null;

  const { data: companyByEmbedKey, isLoading: isLoadingEmbedKey } =
    useGetCompanyByEmbedKey(shouldFetchByEmbedKey);


  // Determine which company data to use. Impersonation (VRG-91) wins: it carries the
  // company_id explicitly, so a Vera staffer entering a brand's space loads that brand
  // rather than falling through to the subdomain/embed-key resolution (which is empty
  // on the bare app URL) and a placeholder id.
  useEffect(() => {
    const companyData = companyBySubdomain || companyByEmbedKey;
    // VRG-809 — `previewCompanyId` sits ahead of the hardcoded id for the Studio-preview case
    // only; it is behind impersonation and behind real resolution, so it can only ever REPLACE
    // the fallback, never override a company the host actually identifies.
    //
    // ⚠️ That trailing literal is the seed company (`COMPANY_ID` in apps/api/src/database/seed.ts
    // — Louis Vuitton). It is a LAST-RESORT fallback that silently renders one real brand's
    // identity whenever resolution fails, which is a brand leak rather than a graceful default,
    // and it should become an explicit "company not found" state. Not changed here, because
    // every unresolved path currently depends on it; see the task.
    const resolvedId =
      impersonationCompanyId ||
      companyData?.id ||
      previewCompanyId ||
      'f0d79e0e-3ff9-4d44-aa98-6609fd264cd7';

    setCompanyId(resolvedId);
  }, [companyBySubdomain, companyByEmbedKey, impersonationCompanyId, previewCompanyId]);

  // Check if subdomain was provided but not found
  const subdomainNotFound =
    subdomain && !isLoadingSubdomain && !companyBySubdomain && subdomainError;

  useEffect(() => {
    // Handle impersonation token passed via query param from apps/platform
    const impersonationToken = url.searchParams.get('impersonation_token');
    if (impersonationToken) {
      // Store as accessToken so subsequent API calls use it
      storage.setItem('accessToken', impersonationToken);
      // Strip from URL to avoid leakage via browser history / referrer
      url.searchParams.delete('impersonation_token');
      const cleanUrl = url.toString();
      window.history.replaceState({}, document.title, cleanUrl);
    }

    // VERA-838: Shopify auto-login hardening. When a fresh shopify_token
    // arrives with certificate_data, the auto-login attempt must fully
    // resolve (success, fallback-to-existing-session, or a visible error)
    // before isLoading flips false and the router renders. The previous
    // code dispatched `checkAuthAction` unconditionally right after kicking
    // off the Shopify exchange — checkAuthAction reads localStorage
    // synchronously and resolves almost immediately with no tokens (the
    // exchange hasn't finished its network round trip yet), so isLoading
    // flipped false early and briefly rendered the manual sign-in form
    // before the Shopify session took over (a visible flash).
    if (certificate_data) {
      const paramsData: IGetShopifySearchParams = JSON.parse(atob(`${certificate_data}`));

      dispatch(
        setShopifyUser({
          shopify_user: paramsData.email,
        })
      );
      dispatch(setScanHash(paramsData.hash));
      storage.setItem('redirectTo', paramsData.location);

      if (token) {
        setIsLoading(true);
        dispatch(
          loginShopifyUser({ shopify_token: token as string }, result => {
            // Bug fix: `result` is the full `{ success, text? }` callback
            // payload (IValidationCallbackProps), not a boolean — the old
            // `success => { if (success) {...} }` always took the truthy
            // branch (a non-null object is always truthy), so a failed
            // exchange fell through to reading empty tokens from storage
            // and silently no-op'd after showing a false "success" toast.
            if (result.success) {
              const accessToken = storage.getItem('accessToken', '');
              const redirectTo = storage.getItem('redirectTo', '');

              setIsLoading(false);

              if (isEmpty(accessToken) || isEmpty(redirectTo)) {
                openSnackbar({
                  severity: 'success',
                  title: 'You’ve entered your account successfully',
                });
                return;
              }

              openSnackbar({
                severity: 'success',
                title: `You’ve entered your account successfully`,
              });

              const apiUrl = import.meta.env.REACT_APP_API_URL;

              window.open(
                `${apiUrl}/auth/customer/set-auth?token=${accessToken}&redirectTo=${redirectTo}`,
                '_top'
              );
              return;
            }

            // Shopify auto-login failed (expired/invalid session token, no
            // matching customer, or a network error). Don't strand the user
            // on a blank/loading screen: fall back to checking for an
            // already-valid stored session first, so a returning user with
            // a live session isn't forced through sign-in/OTP again. Only
            // when that also comes up empty do we send them to the manual
            // sign-in surface with a clear, non-silent message.
            dispatch(
              checkAuthAction(checkResult => {
                setIsLoading(false);

                if (!checkResult.success) {
                  navigate(ROUTES.SIGN_IN, {
                    replace: true,
                    state: {
                      authError:
                        "We couldn't sign you in from your store. Please sign in below.",
                    },
                  });
                }
              })
            );
          })
        );

        // The Shopify callback above owns resolving isLoading (and the
        // fallback auth check on failure) — skip the generic dispatch below
        // so the two auth checks never race.
        return;
      }
    }

    dispatch(checkAuthAction(() => setIsLoading(false)));
  }, []);

  const theme = useMemo(() => {
    const shadowRoot = getShadowRoot();

    if (!companyTheme || isCompanyThemeLoading) {
      return creteMuiTheme(null, shadowRoot);
    }

    return creteMuiTheme(companyTheme.styles, shadowRoot);
  }, [companyTheme, isCompanyThemeLoading]);

  useEffect(() => {
    applyTailwindThemeColors(theme);
  }, [theme]);

  useEffect(() => {
    if (url.searchParams.get('isPreview')) {
      url.searchParams.delete('isPreview');
      navigate(ROUTES.PREVIEW);
    }

    const certificateId = url.searchParams.get('certificate_id');
    if (certificateId) {
      // VERA-455: carry the scan-verification token (`vt`) onto the SPA route so
      // the certificate page can prove the render was reached via a genuine
      // physical scan (and show "Authentic"). A cloned URL has no valid vt.
      const vt = url.searchParams.get('vt');
      // VRG-138: carry the claim/transfer token from the transfer/gift-init
      // email link onto the SPA route so the certificate page can complete
      // the claim without a physical re-scan. Re-emitted as `claim_token`
      // (NOT `token`) — `ScanTag` (components/pages/certificates/productPage/
      // ScanTag.tsx) already reads its OWN unrelated `token` query param for
      // mobile-scan-session finalization; reusing the same name would collide.
      const claimToken = url.searchParams.get('token');
      url.searchParams.delete('certificate_id');
      url.searchParams.delete('vt');
      url.searchParams.delete('token');
      const destParams = new URLSearchParams();
      if (vt) destParams.set('vt', vt);
      if (claimToken) destParams.set('claim_token', claimToken);
      const destQuery = destParams.toString();
      navigate('/certificates/' + certificateId + (destQuery ? `?${destQuery}` : ''));
    }
  }, [url.searchParams.get('isPreview'), url.searchParams.get('certificate_id')]);

  useEffect(() => {
    embeddedAppPageCorrections();
  }, []);

  // Both entry documents paint a full-viewport scan loader before this bundle
  // exists — apps/app/index.html (Vera-hosted) and the widget page body rendered
  // from widget-installation.hbs (Shopify). It lives in the LIGHT dom, outside
  // this component's shadow root, so `dismissScanLoader` addresses it via plain
  // `document`. Drop it once the app has resolved its loading state and can show
  // real content.
  //
  // EVERY path is the same path: the cover fades and goes. The certificate route
  // used to be special-cased (the loader was held open so its circle could fly
  // into the authenticity badge) — that flight is gone, and the badge now animates
  // its own entrance instead. See utils/scanLoader and components/UI/AuthenticChip.
  //
  // Both ids are handled on purpose. `vera-widget-preload-overlay` is the older
  // markup, and it is still baked into the Shopify Page body of every merchant
  // installed before the rename — that HTML was written once at install time via
  // pageCreate and is never re-rendered, so those pages will keep sending the old
  // id indefinitely. Dropping the old selector would strand them behind a white
  // sheet until the 8s CSS failsafe fired.
  //
  // VRG-529 — the cover comes down when BOTH waits are over, not just the auth one.
  //
  // This effect used to read `if (!isLoading) dismissScanLoader()`. `isLoading` is
  // ONLY the auth-check state (declared at the top of this component, resolved by
  // the `checkAuthAction` dispatch below), and `checkAuthAction` reads localStorage
  // synchronously — so it resolves almost immediately, typically well before the
  // `useGetCompanyStyles` request that produces the theme has come back. The render
  // path below then hits `if (isCompanyThemeLoading)` and returns a loader.
  //
  // So the branded cover was being torn down mid-load and the user was handed the
  // remainder of the wait on a bare, UNTHEMED spinner — unthemed structurally, not
  // by oversight: that early return sits ABOVE this component's `<ThemeProvider>`,
  // so nothing it renders can read the brand's colours even once they arrive.
  //
  // Holding the cover is the fix rather than theming that spinner, because the cover
  // is the thing already on screen and already correct. Swapping one loading
  // treatment for another mid-wait reads as the page restarting, which is the exact
  // failure `LogoLoader`'s docblock and the scan-loader module are both written
  // around.
  //
  // No label write happens here. An earlier draft of this change advanced the cover's
  // copy at this exact point — auth settled, theme still in flight — which read as a
  // sensible progress signal and was not: both halves of this wait are plumbing
  // (a localStorage read, then a styles fetch), neither is a phase a consumer can
  // name, and loader.css holds the label invisible until 1200ms so the swap had
  // almost always already happened before anything was on screen. The copy is now
  // written once, at bundle eval, and says where the load is GOING rather than which
  // internal step it is on. See `utils/scanLoader`'s docblock for the full argument.
  useEffect(() => {
    if (isLoading || isCompanyThemeLoading) return;

    dismissScanLoader();
  }, [isLoading, isCompanyThemeLoading]);

  // NOTE: this hook MUST stay above the early returns below. Declaring it after
  // a conditional `return` makes its call order depend on render-path (it was
  // skipped while company styles were loading, then called once they settled),
  // which trips React's Rules of Hooks and unmounts the whole App to a blank
  // screen. Keep all hooks before the first early return.
  const handleImpersonationExit = useCallback(() => {
    // Redirect back to platform after exit
    const platformUrl = import.meta.env.REACT_APP_PLATFORM_URL ?? 'http://localhost:5173';
    window.location.href = `${platformUrl}/internal/companies`;
  }, []);

  // VRG-529: the branded loader, inside a provider, instead of a bare
  // `<CircularProgress>` outside one.
  //
  // The theme has not arrived at this point, so `theme` here is the UNBRANDED one
  // (`creteMuiTheme(null, …)` in the memo above) and `LogoLoader` correctly draws
  // the Vera V in the pre-boot cover's own #272727. That is the intended reading:
  // this branch is the window in which nobody yet knows whose page this is.
  //
  // ⚠️ On a normal scan the user does NOT see this. The dismissal effect above drops
  // the cover on exactly `!isLoading && !isCompanyThemeLoading`, which is the exact
  // complement of the condition mounting this branch and the ternary below — so on
  // both host documents that carry the snippet, this renders underneath the cover for
  // its whole life. It is a FALLBACK surface, reachable only after the cover's 8s CSS
  // failsafe hides it, on the legacy `vera-widget-preload-overlay` markup (removed
  // outright rather than faded), or in a host document with no snippet at all. The
  // reason to render it anyway is that those three used to get a bare, unthemable
  // `<CircularProgress>`; they now get the same mark and ring the cover was showing.
  // The branded scan wait itself belongs to VRG-534's `/verify` interstitial, not to
  // this. See `LogoLoader`'s docblock.
  if (isCompanyThemeLoading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LogoLoader />
      </ThemeProvider>
    );
  }

  // Show error if subdomain not found
  if (subdomainNotFound) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            padding: '20px',
            textAlign: 'center',
          }}
        >
          <h1 style={{ fontSize: '48px', marginBottom: '16px' }}>404</h1>
          <h2 style={{ fontSize: '24px', marginBottom: '16px' }}>Company Not Found</h2>
          <p style={{ fontSize: '16px', color: '#666', maxWidth: '500px' }}>
            The subdomain "<strong>{subdomain}</strong>" does not exist or has been removed.
          </p>
          <p style={{ fontSize: '14px', color: '#999', marginTop: '16px' }}>
            Please check the URL and try again, or contact support if you believe this is an error.
          </p>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <>
      <ImpersonationBanner onExit={handleImpersonationExit} />
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {isLoading ? (
          /* VRG-529: branded, and inside the provider, so it takes the brand's own
             accent and mark. The theme HAS resolved by the time this branch is
             reachable (the `isCompanyThemeLoading` early return above is what
             guarantees that), so `companyTheme.styles.logo` is the real value or is
             genuinely absent — never merely not-yet-fetched. */
          <LogoLoader logoUrl={companyTheme?.styles?.logo} />
        ) : (
          /*
           * Inside ThemeProvider, wrapping ONLY the routed content.
           *
           * A render error in any page is caught here and shown as a recoverable
           * fallback instead of unmounting the whole app to a white screen — which
           * is exactly what a single missing import in ScanTag did (React was
           * logging "Consider adding an error boundary" throughout).
           *
           * Placed inside the providers so the store, CompanyContext and the theme
           * survive a page crash and the fallback renders THEMED. Outside them it
           * would take the app shell down with the page and render unstyled.
           *
           * Known gap: a throw in App's OWN render (the theme useMemo, the auth
           * effects) is above this boundary and still white-screens. Catching that
           * needs a boundary around <App /> in index.tsx.
           */
          <ErrorBoundary>
            {/*
              VRG-418 — ONE TermSheet for the whole app, mounted here.

              Inside the router (it calls `useNavigate` for its "Learn more" link) and
              inside ThemeProvider (it is a themed sheet like every other). Inside the
              ErrorBoundary too, so a throw in the sheet is caught with the rest of the
              routed content rather than white-screening the app.

              The single instance is the MECHANISM, not a performance choice: with one
              sheet and one id, a second `openTerm` replaces the first, so two term sheets
              can never stack (§52). See `TermSheetProvider` for the full note.
            */}
            <TermSheetProvider>
            <Routes>
            <Route element={isIframe ? <LayoutFrame /> : <LayoutDefault />}>
              <Route
                path={ROUTES.HOME}
                element={
                  <Navigate to={embeddKey ? ROUTES.SIGN_IN : ROUTES.CERTIFICATES} replace />
                }
              />

              <Route element={<ProtectedRoute />}>
                <Route path={ROUTES.CERTIFICATES} element={<CertificatesPage />} />
                {/* VRG-455 — a purchase awaiting its scan. Inside ProtectedRoute
                    because a pending sale is bound to an authenticated buyer:
                    resolution is keyed on identity, so there is nothing here to
                    show a signed-out visitor. */}
                <Route path={ROUTES.PENDING_SALE} element={<PendingSalePage />} />
                <Route path={ROUTES.PROFILE} element={<ProfilePage />} />
                {/* VERA-304: signed-in only — it shows YOUR code and rotates it.
                    Not in STANDALONE_ENTRY_PATHS: it is only ever pushed from
                    Profile, never reached as a browser URL (that is `/u/:code`). */}
                <Route path={ROUTES.PROFILE_MY_CODE} element={<MyCodePage />} />
                <Route path={ROUTES.ACTIVITY} element={<ActivityPage />} />
                <Route path={ROUTES.NOTIFICATIONS} element={<NotificationsPage />} />
                {/* VERA-298: owner-only, so it sits inside ProtectedRoute unlike
                    the publicly-scannable certificate route below. */}
                <Route path={ROUTES.CERTIFICATE_SHARE} element={<SharePage />} />
                {/* VERA-315 Phase 4: the certificate's Provenance / Scans /
                    Activity tabs, as pushed screens. Owner-only for the same
                    reason Share is — the tabs they replace only ever rendered
                    in the owner view. */}
                <Route path={ROUTES.CERTIFICATE_PROVENANCE} element={<ProvenanceSubView />} />
                <Route path={ROUTES.CERTIFICATE_SCANS} element={<ScansSubView />} />
                <Route path={ROUTES.CERTIFICATE_ACTIVITY} element={<ActivitySubView />} />
                <Route path={ROUTES.CERTIFICATE_EXPERIENCE} element={<ExperienceSubView />} />
              </Route>

              <Route element={<ProtectedAuthRoute />}>
                {/* VERA-837: both paths render the redesigned SignInBanner —
                    SIGN_IN_BANNER kept as a back-compat alias for existing
                    deep links / bookmarks. */}
                <Route path={ROUTES.SIGN_IN} element={<SignInBanner />} />
                <Route path={ROUTES.SIGN_IN_BANNER} element={<SignInBanner />} />
                {/* VERA-837: dedicated Forgot password page */}
                <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPassword />} />
              </Route>

              <Route path={ROUTES.AUTH_CALLBACK} element={<AuthCallback />} />

              {/* VERA-1095: public self-signup (unauthenticated) */}
              <Route path={ROUTES.SIGNUP} element={<SignupPage />} />
              {/* VERA-1095 Slice 4: Checkout success return — verify + activate */}
              <Route path={ROUTES.SIGNUP_SUCCESS} element={<SignupSuccessPage />} />

              {/* VERA-843: Account Email Path — "create account" invite landing route */}
              <Route path={ROUTES.CREATE_ACCOUNT} element={<CreateAccountEmail />} />
              {/* VERA-839: Claim-Ownership Sign-Up — "claim ownership" invite landing route */}
              <Route path={ROUTES.CLAIM} element={<ClaimOwnershipSignUp />} />

              <Route path={ROUTES.COMPANY_SUSPENDED} element={<CompanySuspended />} />

              {/*
                VRG-358 — deliberately OUTSIDE <ProtectedRoute>. Someone
                scanning a friend's QR with their phone camera may have no Vera
                account at all, and bouncing them to sign-in to be told who they
                just scanned defeats the purpose of the code. The page reveals
                only what a @handle lookup already reveals to any signed-in
                user, so there is nothing here that authentication would protect.
              */}
              <Route path={ROUTES.PROFILE_CODE} element={<ProfileCode />} />

              {/*
                VRG-418 — Help is PUBLIC, deliberately outside <ProtectedRoute>.

                The certificate route below is public because a stranger who taps an NFC
                tag has no account, and that stranger is precisely the reader who needs
                "what does verified mean" and "how do I check an item". Gating Help behind
                sign-in would lock the explanations away from the person they were written
                for, on the one screen where they are being asked to trust something.

                Nothing here is account-scoped: both corpora are committed artifacts and
                no request is made, so there is no data an anonymous reader could reach
                that they should not.
              */}
              <Route path={ROUTES.HELP} element={<HelpPage />} />
              <Route path={ROUTES.HELP_ARTICLE} element={<HelpArticlePage />} />

              <Route path={ROUTES.INVALID_SCANS} element={<InvalidScans />} />

              {/* VRG-268: where a retired (replaced) tag lands. Sits beside INVALID_SCANS
                  because it is the same kind of surface — a physical tap that cannot resolve,
                  given a real page instead of a bare error. */}
              <Route path={ROUTES.TAG_REPLACED} element={<TagReplaced />} />

              {/* VRG-275: where a HELD replacement tag lands (mailed to the owner, bound but
                  not yet live). Public/unauthenticated by necessity, same as TAG_REPLACED —
                  the page itself carries the "sign in to confirm receipt" claim flow. */}
              <Route path={ROUTES.TAG_NOT_ACTIVE} element={<TagNotActive />} />

              {/* VRG-319: where a genuine tag lands when its CERTIFICATE was never
                  activated. Sibling of the two above, and deliberately a separate route
                  from TAG_NOT_ACTIVE: that page is about a held TAG and offers the owner a
                  claim action, this one is about an unissued CERTIFICATE and offers none.
                  ⚠️ Registering it here is only half the wiring — it must also be in
                  STANDALONE_ENTRY_PATHS in `index.tsx`, or MemoryRouter ignores the path
                  and bounces the visitor to sign-in. */}
              <Route path={ROUTES.CERTIFICATE_NOT_ACTIVE} element={<CertificateNotActive />} />

              {/* Where an UNEXPECTED server error during `GET /verify/:global_tag_id` lands.
                  The residual case after the three routes above, which each cover a KNOWN
                  outcome of a tap: this one is what the global exception filter falls through
                  to, and it exists because that fall-through used to answer raw JSON that iOS
                  Safari offered to real customers as a file download.
                  ⚠️ Same half-wiring trap as its siblings — it must ALSO be in
                  STANDALONE_ENTRY_PATHS in `index.tsx`, or MemoryRouter ignores the path and
                  bounces the visitor to sign-in. */}
              <Route path={ROUTES.SOMETHING_WENT_WRONG} element={<SomethingWentWrong />} />

              {/* THERE IS NO `/certificates/:id/access` ROUTE, and this note exists so
                  the next person does not add one for a third time.

                  A scan lands on the CERTIFICATE. Where the brand gates it, the gate is a
                  state card rendered on that certificate directly under the title, with
                  the product details behind it blurred — the paywall shape a visitor
                  already knows how to read: there is something here, and this is what it
                  costs. A standalone page cannot say that. It withholds the page AND the
                  evidence of what the page holds, so the ask arrives with nothing behind
                  it, which is the shape of a phishing form rather than a gate.

                  It has been built as a page twice and reverted twice — VERA-315 (D), then
                  5f6eeca50 — and each time the second surface drifted from the first: the
                  page shipped in VERA-71 consulted the brand's access settings nowhere and
                  hard-required a location the brand may have switched off. The gate's whole
                  behaviour lives in ONE component with ONE call site,
                  `CertificatePage/components/RequestAccessInfo`. A route means a second
                  presentation of it, and that is what keeps drifting (§36 / §47).

                  `ROUTES.CERTIFICATE_ACCESS` is gone from `routes.ts` with it, so the path
                  is not merely unrouted but unnameable. */}
              <Route path={ROUTES.CERTIFICATE} element={<CertificatePage />} />
            </Route>

            <Route path={ROUTES.PREVIEW} element={<StudioPreviewPage />} />

            <Route path='*' element={<NotFound />} />
            </Routes>
            </TermSheetProvider>
          </ErrorBoundary>
        )}
      </ThemeProvider>
    </>
  );
};

export default App;
